import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SERVICES } from "../lib/content";

export default function Services() {
const testimonials = [
  {
    quote:
      "Accrue Metrics brought structure and consistency to our bookkeeping workflows.",
    role: "Managing Partner",
    company: "CPA Firm",
  },
  {
    quote:
      "Reliable execution and clear communication made the engagement seamless.",
    role: "Operations Lead",
    company: "Tax Practice",
  },
  {
    quote:
      "The support helped reduce operational workload for our internal team.",
    role: "Director",
    company: "Accounting Firm",
  },
];

const [currentTestimonial, setCurrentTestimonial] = useState(0);

useEffect(() => {
  const interval = setInterval(() => {
    setCurrentTestimonial((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  }, 4000);

  return () => clearInterval(interval);
}, []);

  return (
    <div data-testid="page-services">
      {/* HEADER */}
      <section className="bg-[#0A1F44] py-24 text-white lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">Services</div>
              <h1 className="mt-4 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
                Five services.
                <br />
                <span className="italic text-[#C9A227]">One operating system</span>
                <br />
                for your back office.
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg leading-relaxed text-white/75">
                We deliver each service modularly or as a connected stack — pick the entry point
                that fits the season your firm is in, and scale up when you're ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES DETAIL */}
      {SERVICES.map((service, i) => (
        <section
          key={service.id}
          id={service.id}
          data-testid={`services-detail-${service.id}`}
          className={`py-24 lg:py-32 ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAFB]"}`}
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className={`grid items-center gap-14 lg:grid-cols-12 ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative">
                  <div className="absolute -inset-4 -rotate-1 border border-[#C9A227]/40" />
                  <img
                    src={service.image}
                    alt={service.title}
                    className="relative h-[440px] w-full rounded-sm object-cover"
                  />
                </div>
              </div>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
                  Service · 0{i + 1}
                </div>
                <h2 className="mt-3 font-serif text-4xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-5xl">
                  {service.title}
                </h2>
                <p className="mt-5 font-serif text-2xl text-[#0A1F44]/80">{service.short}</p>
                <p className="mt-5 text-base leading-relaxed text-[#475467]">{service.description}</p>

                <ul className="mt-8 space-y-3">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#C9A227]/15">
                        <Check className="h-3 w-3 text-[#C9A227]" strokeWidth={3} />
                      </span>
                      <span className="text-sm text-[#0A1F44]">{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10">
                  <Link
                    to="/contact"
                    data-testid={`services-cta-${service.id}`}
                    className="hover-lift inline-flex items-center gap-3 rounded-sm bg-[#0A1F44] px-6 py-3 text-sm font-semibold text-white hover:bg-[#071630]"
                  >
                    Talk about {service.title}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* PRICING / ENGAGEMENT MODELS */}
      <section className="bg-[#0A1F44] py-24 text-white lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">Engagement models</div>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-white sm:text-5xl">
              Pick the cadence that matches your firm.
            </h2>
          </div>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {[
              {
                name: "Per-Client Bookkeeping",
                price: "From $399",
                period: "/client / month",
                desc: "Best for tax-focused firms who want clean books delivered every month for an existing client portfolio.",
                features: ["Monthly close", "Up to 200 transactions", "GL & reporting package", "Dedicated lead reviewer"],
              },
              {
                name: "Embedded Team",
                price: "From $3,499",
                period: "/month",
                desc: "Best for growing firms — a dedicated 2–4 person pod that operates as your back office across services.",
                features: ["FTE-equivalent capacity", "Bookkeeping + automation", "Slack/email same-day SLA", "Quarterly QBR"],
                featured: true,
              },
              {
                name: "AI + Pod Hybrid",
                price: "Custom",
                period: "scoped per firm",
                desc: "Best for forward-leaning firms ready to deploy supervised AI agents alongside an embedded delivery pod.",
                features: ["Custom AI workflows", "Anomaly detection", "All Embedded Team features", "Named relationship lead"],
              },
            ].map((p) => (
              <div
                key={p.name}
                data-testid={`pricing-card-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`group relative flex flex-col rounded-sm border p-8 transition-all hover:-translate-y-1 ${
                  p.featured
                    ? "border-[#C9A227] bg-white text-[#0A1F44] shadow-2xl shadow-[#C9A227]/20"
                    : "border-white/15 bg-white/[0.04] text-white hover:border-white/30 hover:bg-white/[0.07]"
                }`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-8 rounded-sm bg-[#C9A227] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#0A1F44]">
                    Most picked
                  </div>
                )}
                <h3 className="font-serif text-2xl font-semibold">{p.name}</h3>
                <div className="mt-5 flex items-end gap-2">
                  <span className="font-serif text-4xl font-semibold">{p.price}</span>
                  <span className={`mb-1 text-xs ${p.featured ? "text-[#475467]" : "text-white/60"}`}>{p.period}</span>
                </div>
                <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-[#475467]" : "text-white/70"}`}>
                  {p.desc}
                </p>
                <ul className="mt-6 flex-1 space-y-2.5 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-[#C9A227]" : "text-[#C9A227]"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`pricing-cta-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`hover-lift mt-8 inline-flex items-center justify-center gap-2 rounded-sm px-5 py-3 text-sm font-semibold ${
                    p.featured
                      ? "bg-[#0A1F44] text-white hover:bg-[#071630]"
                      : "border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-[#0A1F44]"
                  }`}
                >
                  Get a quote
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>

      </section>
{/* SERVICES TESTIMONIAL */}
<section className="bg-[#F8F9FC] py-20 lg:py-24">
  <div className="mx-auto max-w-4xl px-6 lg:px-8">

    <div className="rounded-md border border-[#E5E7EB] bg-white p-10 shadow-sm transition-all duration-500 hover:shadow-xl">

      <div className="text-sm uppercase tracking-[0.22em] text-[#C9A227]">
        Client Feedback
      </div>

      <div className="mt-5 text-5xl leading-none text-[#C9A227]">
        “
      </div>

      <p className="mt-5 font-serif text-2xl leading-relaxed text-[#0A1F44]">
        {testimonials[currentTestimonial].quote}
      </p>

      <div className="mt-8">
        <div className="font-semibold text-[#0A1F44]">
          {testimonials[currentTestimonial].role}
        </div>

        <div className="mt-1 text-sm uppercase tracking-[0.18em] text-[#667085]">
          {testimonials[currentTestimonial].company}
        </div>
      </div>

      {/* Dots */}
      <div className="mt-8 flex gap-3">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentTestimonial(index)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              currentTestimonial === index
                ? "w-8 bg-[#C9A227]"
                : "w-2.5 bg-[#D0D5DD]"
            }`}
          />
        ))}
      </div>

    </div>
  </div>
</section>    
</div>
  );
}
